(function habitatStudioModule(root, factory) {
  const dependencies = typeof module === 'object' && module.exports
    ? {
        engine: require('./corkopia-habitat-engine.js'),
        agents: require('./corkopia-customer-agents.js')
      }
    : {
        engine: root.CorkopiaHabitatEngine,
        agents: root.CorkopiaCustomerAgents
      };
  const api = factory(root, dependencies);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root?.document) {
    root.CorkopiaHabitatStudio = api;
    api.prepare(root.document);
  }
})(typeof window !== 'undefined' ? window : globalThis, (root, dependencies) => {
  'use strict';

  const { engine, agents } = dependencies;
  const STORAGE_KEY = 'corkopia-habitat-build-v1';
  const HANDOFF_KEY = 'corkopia-habitat-smart-box-handoff-v1';
  const MAX_HISTORY = 40;
  const VIEWS = ['front', 'top', 'side', 'perspective'];
  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
  const clone = (value) => JSON.parse(JSON.stringify(value));
  const finite = (value) => Number.isFinite(Number(value)) ? Number(value) : null;
  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, Number(value) || 0));

  const createState = () => ({
    version: 1,
    step: 1,
    enclosure: null,
    usableSpace: null,
    profileId: 'other',
    interview: { goals: [], style: 'natural_forest', budgetCents: null, maximumPieces: 6 },
    layout: null,
    originalLayout: null,
    selectedId: null,
    view: 'front',
    zoom: 1,
    orbit: -8,
    layers: { grid: true, protected: true, measurements: true },
    concept: engine?.createConcept?.(null) || null,
    substitutions: [],
    notifyIntent: false,
    savedAt: null
  });

  const sanitizePlacement = (placement = {}) => ({
    id: String(placement.id || placement.variantId || ''),
    pieceId: String(placement.pieceId || ''),
    productId: String(placement.productId || ''),
    variantId: String(placement.variantId || ''),
    handle: String(placement.handle || ''),
    title: String(placement.title || 'Exact cork piece'),
    family: placement.family || null,
    image: placement.image || null,
    priceCents: finite(placement.priceCents),
    role: placement.role || 'structure',
    selectionReasons: Array.isArray(placement.selectionReasons) ? placement.selectionReasons.slice(0, 10) : [],
    dimensions: {
      width: finite(placement.dimensions?.width),
      depth: finite(placement.dimensions?.depth),
      height: finite(placement.dimensions?.height),
      unit: 'in'
    },
    geometry: {
      type: 'BOUNDING_BOX_PROXY',
      honestProxy: true,
      irregularShapeNotRepresented: true
    },
    transform: {
      position: {
        x: Math.max(0, finite(placement.transform?.position?.x) || 0),
        y: Math.max(0, finite(placement.transform?.position?.y) || 0),
        z: Math.max(0, finite(placement.transform?.position?.z) || 0)
      },
      rotation: {
        x: finite(placement.transform?.rotation?.x) || 0,
        y: finite(placement.transform?.rotation?.y) || 0,
        z: finite(placement.transform?.rotation?.z) || 0
      },
      locked: placement.transform?.locked === true
    },
    fit: placement.fit || null,
    openingRequired: placement.openingRequired === true,
    openingVerified: placement.openingVerified === true
  });

  const sanitizeState = (input = {}) => {
    const fallback = createState();
    const layout = input.layout && Array.isArray(input.layout.placements)
      ? {
          ...input.layout,
          placements: input.layout.placements.slice(0, 60).map(sanitizePlacement)
        }
      : null;
    return {
      ...fallback,
      step: [1, 2, 3].includes(Number(input.step)) ? Number(input.step) : 1,
      enclosure: input.enclosure || null,
      usableSpace: input.usableSpace || null,
      profileId: engine.SPECIES_PROFILES.profiles.some((entry) => entry.id === input.profileId) ? input.profileId : 'other',
      interview: {
        goals: Array.isArray(input.interview?.goals) ? [...new Set(input.interview.goals)].slice(0, 12) : [],
        style: String(input.interview?.style || 'natural_forest'),
        budgetCents: finite(input.interview?.budgetCents),
        maximumPieces: clamp(finite(input.interview?.maximumPieces) || 6, 1, 12)
      },
      layout,
      originalLayout: input.originalLayout && Array.isArray(input.originalLayout.placements)
        ? { ...input.originalLayout, placements: input.originalLayout.placements.slice(0, 60).map(sanitizePlacement) }
        : layout ? clone(layout) : null,
      selectedId: layout?.placements.some((placement) => placement.id === input.selectedId) ? input.selectedId : null,
      view: VIEWS.includes(input.view) ? input.view : 'front',
      zoom: [0.75, 1, 1.25, 1.5].includes(Number(input.zoom)) ? Number(input.zoom) : 1,
      orbit: clamp(finite(input.orbit) ?? -8, -45, 45),
      layers: {
        grid: input.layers?.grid !== false,
        protected: input.layers?.protected !== false,
        measurements: input.layers?.measurements !== false
      },
      concept: input.concept && engine.CONCEPT_STATES.includes(input.concept.state)
        ? { ...engine.createConcept(layout, input.concept.state), id: input.concept.id || null }
        : engine.createConcept(layout),
      substitutions: Array.isArray(input.substitutions) ? input.substitutions.slice(-20) : [],
      notifyIntent: input.notifyIntent === true,
      savedAt: input.savedAt || null
    };
  };

  const readState = (storage) => {
    try {
      return sanitizeState(JSON.parse(storage?.getItem(STORAGE_KEY) || 'null') || {});
    } catch (_) {
      return createState();
    }
  };

  const saveState = (storage, state) => {
    try {
      const saved = sanitizeState({ ...state, savedAt: new Date().toISOString() });
      storage?.setItem(STORAGE_KEY, JSON.stringify(saved));
      return saved;
    } catch (_) {
      return sanitizeState(state);
    }
  };

  const catalogProducts = (studio) => {
    const products = [...studio.querySelectorAll('[data-habitat-candidate-source]')]
      .map((host) => host.corkopiaProduct)
      .filter(Boolean);
    return root.CorkopiaDynamicInventory?.selectInventory
      ? root.CorkopiaDynamicInventory.selectInventory(products, {
          surface: 'habitat_intelligence',
          surface_id: studio.dataset.inventorySurfaceId || 'habitat-intelligence',
          exclusions: {}
        }).items
      : products.filter(engine.publicEligible);
  };

  const featureValue = (form, name, unit) => {
    const value = Math.max(0, finite(form.elements[name]?.value) || 0);
    return unit === 'cm' ? value / 2.54 : value;
  };

  const enclosureFromForm = (form) => {
    const unit = form.elements.habitat_unit.value;
    const zoneKind = form.elements.habitat_zone_kind?.value || '';
    const zone = zoneKind ? {
      id: `customer-${zoneKind}`,
      label: form.elements.habitat_zone_label?.value || engine.humanize(zoneKind),
      x: featureValue(form, 'habitat_zone_x', unit),
      y: featureValue(form, 'habitat_zone_y', unit),
      z: featureValue(form, 'habitat_zone_z', unit),
      width: featureValue(form, 'habitat_zone_width', unit),
      height: featureValue(form, 'habitat_zone_height', unit),
      depth: featureValue(form, 'habitat_zone_depth', unit)
    } : null;
    return engine.createEnclosure({
      catalogId: form.elements.habitat_catalog.value,
      model: form.elements.habitat_model?.value,
      unit,
      internalDimensions: {
        width: form.elements.habitat_width.value,
        depth: form.elements.habitat_depth.value,
        height: form.elements.habitat_height.value
      },
      customerConfirmed: form.elements.habitat_confirmed.checked,
      featureUnit: 'in',
      access: {
        type: form.elements.habitat_access.value,
        doorClearance: featureValue(form, 'habitat_door_clearance', unit),
        lidClearance: featureValue(form, 'habitat_lid_clearance', unit),
        serviceClearance: 0.25
      },
      features: {
        drainageHeight: featureValue(form, 'habitat_drainage', unit),
        substrateHeight: featureValue(form, 'habitat_substrate', unit),
        backgroundDepth: featureValue(form, 'habitat_background', unit),
        sideClearance: featureValue(form, 'habitat_side_clearance', unit),
        topClearance: featureValue(form, 'habitat_top_clearance', unit),
        frontClearance: featureValue(form, 'habitat_front_clearance', unit),
        vents: zoneKind === 'vent' && zone ? [zone] : [],
        equipment: zoneKind === 'equipment' && zone ? [zone] : [],
        noBuildZones: zoneKind === 'decor' && zone ? [{ ...zone, reason: 'DECOR_CLEARANCE' }] : []
      }
    });
  };

  const validateEnclosure = (form) => {
    const enclosure = enclosureFromForm(form);
    const usableSpace = engine.calculateUsableSpace(enclosure);
    if (!enclosure.customerConfirmed) return { error: 'Confirm that these are internal measurements.' };
    if (usableSpace.status !== 'READY') return { error: 'Enter complete internal dimensions and leave some usable space.' };
    return { enclosure, usableSpace };
  };

  const prepare = (doc) => {
    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      root.setTimeout(() => {
        scheduled = false;
        doc.querySelectorAll('[data-habitat-studio]:not([data-habitat-ready])').forEach(boot);
      }, 0);
    };
    doc.addEventListener('DOMContentLoaded', schedule);
    doc.addEventListener('corkopia:products-ready', schedule);
    if (doc.readyState !== 'loading') schedule();
  };

  const boot = (studio) => {
    studio.dataset.habitatReady = 'true';
    const $ = (selector, scope = studio) => scope.querySelector(selector);
    const $$ = (selector, scope = studio) => [...scope.querySelectorAll(selector)];
    const form = studio;
    let state = readState(root.localStorage);
    let history = [];
    let future = [];
    let photoUrl = null;
    let drag = null;
    const status = $('[data-habitat-status]');
    const announce = (message) => {
      status.textContent = '';
      root.setTimeout(() => { status.textContent = message; }, 10);
    };
    const showError = (step, message = '') => {
      const error = $(`[data-habitat-step="${step}"] [data-habitat-error]`);
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    };
    const persist = () => { state = saveState(root.localStorage, state); };

    const restoreForm = () => {
      if (!state.enclosure) return;
      const enclosure = state.enclosure;
      const catalog = form.querySelector(`[name="habitat_catalog"][value="${CSS.escape(enclosure.catalogId)}"]`);
      if (catalog) catalog.checked = true;
      form.elements.habitat_model.value = enclosure.model || '';
      form.elements.habitat_width.value = enclosure.internalDimensions?.width || '';
      form.elements.habitat_depth.value = enclosure.internalDimensions?.depth || '';
      form.elements.habitat_height.value = enclosure.internalDimensions?.height || '';
      form.elements.habitat_confirmed.checked = enclosure.customerConfirmed === true;
      form.elements.habitat_access.value = enclosure.access?.type || 'front_doors';
      form.elements.habitat_drainage.value = enclosure.features?.drainageHeight || 0;
      form.elements.habitat_substrate.value = enclosure.features?.substrateHeight || 0;
      form.elements.habitat_background.value = enclosure.features?.backgroundDepth || 0;
      form.elements.habitat_side_clearance.value = enclosure.features?.sideClearance || 0;
      form.elements.habitat_top_clearance.value = enclosure.features?.topClearance || 0;
      form.elements.habitat_front_clearance.value = enclosure.features?.frontClearance || 0;
      form.elements.habitat_door_clearance.value = enclosure.access?.doorClearance || 0;
      form.elements.habitat_lid_clearance.value = enclosure.access?.lidClearance || 0;
      const restoredZone = enclosure.features?.vents?.[0]
        ? { kind: 'vent', value: enclosure.features.vents[0] }
        : enclosure.features?.equipment?.[0]
          ? { kind: 'equipment', value: enclosure.features.equipment[0] }
          : enclosure.features?.noBuildZones?.[0]
            ? { kind: 'decor', value: enclosure.features.noBuildZones[0] }
            : null;
      if (restoredZone) {
        form.elements.habitat_zone_kind.value = restoredZone.kind;
        form.elements.habitat_zone_label.value = restoredZone.value.label || '';
        ['x', 'y', 'z', 'width', 'height', 'depth'].forEach((field) => {
          form.elements[`habitat_zone_${field}`].value = restoredZone.value[field] || 0;
        });
      }
      const profile = form.querySelector(`[name="habitat_profile"][value="${CSS.escape(state.profileId)}"]`);
      if (profile) profile.checked = true;
      state.interview.goals.forEach((goal) => {
        const input = form.querySelector(`[name="habitat_goals"][value="${CSS.escape(goal)}"]`);
        if (input) input.checked = true;
      });
      form.elements.habitat_style.value = state.interview.style;
      form.elements.habitat_budget.value = state.interview.budgetCents || '';
      form.elements.habitat_piece_limit.value = state.interview.maximumPieces;
    };

    const renderStep = (focus = false) => {
      $$('[data-habitat-step]').forEach((section) => {
        section.hidden = Number(section.dataset.habitatStep) !== state.step;
      });
      $$('[data-habitat-go]').forEach((button) => {
        const step = Number(button.dataset.habitatGo);
        button.disabled = step > state.step || (step === 3 && !state.layout);
        if (step === state.step) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      if (focus) $(`[data-habitat-step="${state.step}"] h2`)?.focus?.();
      if (state.step === 2) renderUsable();
      if (state.step === 3) renderWorkspace();
    };

    const renderUsable = () => {
      const usable = state.usableSpace?.usable;
      const internal = state.usableSpace?.internal;
      $('[data-habitat-usable-summary]').innerHTML = usable ? `
        <div><span>Internal space</span><strong>${internal.width} × ${internal.depth} × ${internal.height} in</strong></div>
        <span aria-hidden="true">→</span>
        <div><span>Usable space</span><strong>${usable.width} × ${usable.depth} × ${usable.height} in</strong></div>
        <button type="button" data-habitat-edit-assumptions>Edit assumptions</button>
      ` : '';
      $('[data-habitat-edit-assumptions]')?.addEventListener('click', () => {
        state.step = 1;
        renderStep(true);
      });
      renderAdaptiveQuestions();
    };

    const renderAdaptiveQuestions = () => {
      const result = engine.interviewQuestions({ profileId: form.elements.habitat_profile.value });
      const host = $('[data-habitat-adaptive-questions]');
      host.innerHTML = `
        <div class="habitat-studio__profile-note">
          <strong>${escapeHtml(result.profile.label)}</strong>
          <span>${escapeHtml(result.profile.status)}</span>
          <p>Version ${escapeHtml(engine.SPECIES_PROFILES.version)}. Production husbandry profiles remain blocked pending domain approval.</p>
        </div>
      `;
    };

    const pushHistory = () => {
      if (!state.layout) return;
      history.push(clone(state.layout.placements));
      history = history.slice(-MAX_HISTORY);
      future = [];
    };

    const revalidate = () => {
      if (!state.layout) return;
      const checked = engine.validateLayout(state.layout.placements, state.usableSpace);
      state.layout = {
        ...state.layout,
        placements: checked.placements,
        fit: {
          status: checked.status,
          counts: checked.counts,
          authority: checked.authority,
          aiOverrideAllowed: false
        }
      };
    };

    const viewAxes = () => {
      if (state.view === 'top') return { horizontal: 'width', vertical: 'depth', x: 'x', y: 'z' };
      if (state.view === 'side') return { horizontal: 'depth', vertical: 'height', x: 'z', y: 'y' };
      return { horizontal: 'width', vertical: 'height', x: 'x', y: 'y' };
    };

    const geometryForView = (placement) => {
      const rotated = engine.rotatedDimensions(placement.dimensions, placement.transform.rotation);
      if (state.view === 'top') return { width: rotated.width, height: rotated.depth };
      if (state.view === 'side') return { width: rotated.depth, height: rotated.height };
      return { width: rotated.width, height: rotated.height };
    };

    const limitsForView = () => {
      const usable = state.usableSpace.usable;
      if (state.view === 'top') return { width: usable.width, height: usable.depth };
      if (state.view === 'side') return { width: usable.depth, height: usable.height };
      return { width: usable.width, height: usable.height };
    };

    const positionForView = (placement) => {
      const position = placement.transform.position;
      if (state.view === 'top') return { x: position.x, y: position.z };
      if (state.view === 'side') return { x: position.z, y: position.y };
      return { x: position.x, y: position.y };
    };

    const fitLabel = (statusValue) => ({
      FIT: 'Fits',
      FIT_WITH_WARNING: 'Fits with a warning',
      DOES_NOT_FIT: 'Does not fit',
      UNKNOWN: 'Fit unknown'
    }[statusValue] || 'Fit unknown');

    const renderStage = () => {
      const stage = $('[data-habitat-stage]');
      const pieces = $('[data-habitat-pieces]');
      const usable = state.usableSpace?.usable;
      if (!stage || !usable || !state.layout) return;
      const limits = limitsForView();
      stage.dataset.view = state.view;
      stage.style.setProperty('--stage-ratio', `${limits.width} / ${limits.height}`);
      stage.style.setProperty('--stage-zoom', state.zoom);
      stage.style.setProperty('--stage-orbit', `${state.orbit}deg`);
      stage.style.width = `${state.zoom * 100}%`;
      stage.style.maxWidth = `${50 * state.zoom}rem`;
      stage.classList.toggle('is-perspective', state.view === 'perspective');
      $('[data-habitat-zoom-label]').textContent = `${Math.round(state.zoom * 100)}%`;
      $('[data-habitat-grid]').hidden = !state.layers.grid;
      $('[data-habitat-protected]').hidden = !state.layers.protected;
      $('[data-habitat-scale]').hidden = !state.layers.measurements;
      $('[data-habitat-scale]').textContent = `${limits.width} × ${limits.height} in · ${engine.humanize(state.view)} view`;
      $$('[data-habitat-view]').forEach((button) =>
        button.setAttribute('aria-pressed', String(button.dataset.habitatView === state.view))
      );
      pieces.innerHTML = state.layout.placements.map((placement) => {
        const position = positionForView(placement);
        const geometry = geometryForView(placement);
        const left = (position.x / limits.width) * 100;
        const bottom = (position.y / limits.height) * 100;
        const width = (geometry.width / limits.width) * 100;
        const height = (geometry.height / limits.height) * 100;
        return `
          <button
            class="habitat-studio__piece is-${String(placement.fit?.status || 'UNKNOWN').toLowerCase().replace(/_/g, '-')}${state.selectedId === placement.id ? ' is-selected' : ''}"
            type="button"
            data-habitat-piece="${escapeHtml(placement.id)}"
            style="--piece-left:${left}%;--piece-bottom:${bottom}%;--piece-width:${width}%;--piece-height:${height}%;"
            aria-label="${escapeHtml(placement.title)}. ${fitLabel(placement.fit?.status)}.${placement.transform.locked ? ' Locked.' : ''}"
            aria-pressed="${state.selectedId === placement.id}"
          >
            ${placement.image ? `<img src="${escapeHtml(placement.image)}" alt="">` : '<span aria-hidden="true"></span>'}
            <small>${escapeHtml(placement.pieceId || placement.title)}</small>
          </button>
        `;
      }).join('');
      renderProtectedZones();
      $('[data-habitat-undo]').disabled = !history.length;
      $('[data-habitat-redo]').disabled = !future.length;
    };

    const renderProtectedZones = () => {
      const host = $('[data-habitat-protected]');
      if (!host || !state.layers.protected) return;
      const limits = limitsForView();
      host.innerHTML = (state.usableSpace.protectedZones || []).map((zone) => {
        const zonePlacement = {
          dimensions: { width: zone.width, depth: zone.depth, height: zone.height },
          transform: { position: { x: zone.x, y: zone.y, z: zone.z }, rotation: {} }
        };
        const position = positionForView(zonePlacement);
        const geometry = geometryForView(zonePlacement);
        return `<span style="--zone-left:${position.x / limits.width * 100}%;--zone-bottom:${position.y / limits.height * 100}%;--zone-width:${geometry.width / limits.width * 100}%;--zone-height:${geometry.height / limits.height * 100}%;" title="${escapeHtml(zone.label)}"></span>`;
      }).join('');
    };

    const selectedPlacement = () => state.layout?.placements.find((placement) => placement.id === state.selectedId) || null;

    const renderInspector = () => {
      const selected = selectedPlacement();
      const host = $('[data-habitat-inspector]');
      const fit = state.layout?.fit;
      $('[data-habitat-fit-summary]').innerHTML = `
        <span>Layout fit</span>
        <strong data-fit="${escapeHtml(fit?.status || 'UNKNOWN')}">${fitLabel(fit?.status)}</strong>
        <small>${escapeHtml(state.layout?.explanation?.confidence || 'Unknown')} confidence · Deterministic fit engine · AI cannot override</small>
      `;
      if (!selected) {
        host.innerHTML = '<p>Select a piece to inspect its dimensions, fit evidence, and controls.</p>';
        return;
      }
      const evidence = [...(selected.fit?.reasons || []), ...(selected.fit?.warnings || [])];
      host.innerHTML = `
        <div class="habitat-studio__inspector-piece">
          ${selected.image ? `<img src="${escapeHtml(selected.image)}" alt="">` : ''}
          <div><strong>${escapeHtml(selected.title)}</strong><span>${escapeHtml(selected.pieceId || selected.variantId)}</span></div>
        </div>
        <dl>
          <div><dt>Bounding dimensions</dt><dd>${selected.dimensions.width || '?'} × ${selected.dimensions.depth || '?'} × ${selected.dimensions.height || '?'} in</dd></div>
          <div><dt>Role</dt><dd>${escapeHtml(engine.humanize(selected.role))}</dd></div>
          <div><dt>Why selected</dt><dd>${(selected.selectionReasons || []).map(engine.humanize).join(', ') || 'Current public eligible exact piece'}</dd></div>
          <div><dt>Fit</dt><dd>${fitLabel(selected.fit?.status)}</dd></div>
          <div><dt>Evidence</dt><dd>${evidence.length ? evidence.map(engine.humanize).join(', ') : 'Inside bounds and clear of protected zones'}</dd></div>
        </dl>
        <div class="habitat-studio__inspector-actions">
          <button type="button" data-habitat-rotate>Rotate 90°</button>
          <button type="button" data-habitat-lock>${selected.transform.locked ? 'Unlock' : 'Lock'}</button>
          <button type="button" data-habitat-replace>Find replacement</button>
          <button type="button" data-habitat-remove>Remove</button>
        </div>
        <p>Move with arrow keys or drag in the active view. Measured boxes are conservative proxies.</p>
      `;
    };

    const renderPieceList = () => {
      const host = $('[data-habitat-piece-list]');
      const empty = $('[data-habitat-empty]');
      const placements = state.layout?.placements || [];
      empty.hidden = placements.length > 0;
      host.hidden = placements.length === 0;
      host.innerHTML = placements.map((placement) => `
        <button type="button" data-habitat-list-piece="${escapeHtml(placement.id)}">
          ${placement.image ? `<img src="${escapeHtml(placement.image)}" alt="">` : '<span></span>'}
          <strong>${escapeHtml(placement.title)}</strong>
          <small>${escapeHtml(engine.humanize(placement.family))} · ${fitLabel(placement.fit?.status)}</small>
        </button>
      `).join('');
    };

    const renderConcept = () => {
      const host = $('[data-habitat-concept]');
      const button = $('[data-habitat-concept-request]');
      const concept = state.concept || engine.createConcept(state.layout);
      host.dataset.state = concept.state;
      if (concept.state === 'COMPLETE') {
        host.innerHTML = `
          <div class="habitat-studio__concept-proxy" aria-label="Local concept placeholder based on the accepted layout">
            ${(state.layout?.placements || []).map((placement, index) =>
              `<span style="--concept-x:${10 + (index * 17) % 72}%;--concept-y:${12 + (index * 23) % 65}%;--concept-r:${(index * 19) % 35 - 17}deg"></span>`
            ).join('')}
          </div>
          <small>${escapeHtml(engine.DISCLAIMER)}</small>
        `;
        button.textContent = 'Refresh concept placeholder';
      } else {
        host.innerHTML = `<span>${escapeHtml(engine.humanize(concept.state))}</span>`;
        button.textContent = concept.state === 'READY' ? 'Generate placeholder' : 'Prepare concept placeholder';
      }
    };

    const renderWorkspace = () => {
      if (!state.layout) return;
      $('[data-habitat-explanation]').textContent = state.layout.explanation?.why || 'Current public exact pieces placed against confirmed usable dimensions.';
      renderStage();
      renderInspector();
      renderPieceList();
      renderConcept();
    };

    const commitPlacementChange = (message) => {
      revalidate();
      persist();
      renderWorkspace();
      announce(message);
      root.document.dispatchEvent(new CustomEvent('corkopia:habitat-layout-updated', {
        detail: {
          layoutId: state.layout.layoutId,
          fitStatus: state.layout.fit.status,
          pieceCount: state.layout.placements.length,
          cartMutation: false
        }
      }));
    };

    const moveSelected = (axis, delta) => {
      const selected = selectedPlacement();
      if (!selected || selected.transform.locked) return;
      pushHistory();
      selected.transform.position[axis] = Math.max(0, selected.transform.position[axis] + delta);
      commitPlacementChange(`${selected.title} moved. ${fitLabel(selected.fit?.status)}.`);
    };

    const generate = () => {
      state.profileId = form.elements.habitat_profile.value;
      state.interview = {
        goals: $$('[name="habitat_goals"]:checked').map((input) => input.value),
        style: form.elements.habitat_style.value,
        budgetCents: finite(form.elements.habitat_budget.value),
        maximumPieces: clamp(finite(form.elements.habitat_piece_limit.value) || 6, 1, 12)
      };
      const products = catalogProducts(studio);
      const result = agents.habitatAgent.run({
        requestId: `habitat-${Date.now()}`,
        enclosure: state.enclosure,
        interview: state.interview,
        products,
        maximumPieces: state.interview.maximumPieces
      });
      state.layout = result.output.layout;
      state.originalLayout = clone(state.layout);
      state.selectedId = state.layout.placements[0]?.id || null;
      state.concept = engine.createConcept(state.layout);
      state.step = 3;
      history = [];
      future = [];
      persist();
      studio.classList.add('is-layout-arriving');
      renderStep(true);
      root.setTimeout(() => studio.classList.remove('is-layout-arriving'), 760);
      announce(state.layout.placements.length
        ? `${state.layout.placements.length} current exact pieces placed. ${fitLabel(state.layout.fit.status)}.`
        : 'No eligible exact pieces are available. Your build intent is still saved locally.');
    };

    const replaceSelected = () => {
      const sold = selectedPlacement();
      if (!sold) return;
      const products = catalogProducts(studio);
      const ranked = engine.rankSubstitutions({
        soldPlacement: sold,
        products,
        usableSpace: state.usableSpace,
        placements: state.layout.placements
      });
      const replacement = ranked[0];
      if (!replacement) {
        announce('No current eligible replacement fits this position.');
        return;
      }
      const approved = root.confirm?.(
        `Replace ${sold.title} with ${replacement.candidate.title}? ${replacement.reasons.map(engine.humanize).join(', ')}.`
      );
      if (!approved) {
        announce('Replacement was not applied.');
        return;
      }
      pushHistory();
      const index = state.layout.placements.findIndex((placement) => placement.id === sold.id);
      state.layout.placements[index] = replacement.candidate;
      state.selectedId = replacement.candidate.id;
      state.substitutions.push({
        fromVariantId: sold.variantId,
        toVariantId: replacement.candidate.variantId,
        confirmed: true,
        reasons: replacement.reasons
      });
      commitPlacementChange('Confirmed replacement applied and fit rechecked.');
    };

    const acceptBuild = () => {
      const current = new Map(catalogProducts(studio).filter(engine.publicEligible)
        .map((product) => [engine.productIdentity(product).variantId, product]));
      const unavailable = state.layout.placements.filter((placement) => !current.has(placement.variantId));
      if (unavailable.length) {
        state.layout.placements = state.layout.placements.map((placement) =>
          unavailable.some((entry) => entry.id === placement.id)
            ? { ...placement, fit: { status: 'UNKNOWN', reasons: ['PIECE_NO_LONGER_AVAILABLE'], warnings: [] } }
            : placement
        );
        persist();
        renderWorkspace();
        announce(`${unavailable.length} exact ${unavailable.length === 1 ? 'piece is' : 'pieces are'} no longer available. Choose replacements before accepting.`);
        return;
      }
      if (state.layout.fit.status === engine.FIT.DOES_NOT_FIT || state.layout.fit.status === engine.FIT.UNKNOWN) {
        announce('Resolve fit failures and unknown measurements before accepting this build.');
        return;
      }
      const profile = engine.SPECIES_PROFILES.profiles.find((entry) => entry.id === state.profileId);
      const dna = engine.createBuildDna({
        previous: root.CorkopiaBuildCanvas?.expandBuildDna?.({}, {}, [], []) || {},
        enclosure: state.enclosure,
        usableSpace: state.usableSpace,
        speciesProfile: profile,
        interview: state.interview,
        layout: state.layout,
        concept: state.concept,
        substitutions: state.substitutions
      });
      const handoff = {
        version: 'habitat-smart-box-handoff-v1',
        source: 'habitat_intelligence',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
        buildDna: dna,
        proposedLines: state.layout.placements.map((placement) => ({
          variantId: placement.variantId,
          quantity: 1,
          pieceId: placement.pieceId,
          customerConfirmationRequired: true
        })),
        cartMutation: null,
        cartAuthority: 'shopify',
        inventoryRevalidated: true
      };
      try {
        root.sessionStorage?.setItem(HANDOFF_KEY, JSON.stringify(handoff));
      } catch (_) {
        announce('The layout is valid, but this browser could not move it into your box.');
        return;
      }
      root.document.dispatchEvent(new CustomEvent('corkopia:habitat-build-accepted', {
        detail: {
          layoutId: state.layout.layoutId,
          proposedVariantIds: handoff.proposedLines.map((line) => line.variantId),
          buildDnaVersion: dna.version,
          cartMutation: false,
          customerConfirmationRequired: true
        }
      }));
      studio.classList.remove('is-build-accepted');
      void studio.offsetWidth;
      studio.classList.add('is-build-accepted');
      root.setTimeout(() => studio.classList.remove('is-build-accepted'), 900);
      root.CorkopiaMyBox?.open?.();
      announce('Your layout is ready to review. Nothing was added to your cart.');
      $('[data-habitat-accept-title]').textContent = 'Ready to review';
    };

    studio.addEventListener('change', (event) => {
      if (event.target.matches('[name="habitat_catalog"]')) {
        $('[data-enclosure-model]').hidden = event.target.value === 'custom';
      }
      if (event.target.matches('[name="habitat_unit"]')) {
        $$('[data-habitat-unit-label]').forEach((label) => { label.textContent = event.target.value; });
      }
      if (event.target.matches('[name="habitat_profile"]')) renderAdaptiveQuestions();
      if (event.target.matches('[data-habitat-layer]')) {
        state.layers[event.target.dataset.habitatLayer] = event.target.checked;
        persist();
        renderStage();
      }
    });

    $('[data-habitat-next]').addEventListener('click', () => {
      const result = validateEnclosure(form);
      if (result.error) {
        showError(1, result.error);
        return;
      }
      showError(1);
      state.enclosure = result.enclosure;
      state.usableSpace = result.usableSpace;
      state.step = 2;
      persist();
      renderStep(true);
      announce(`Usable space confirmed: ${result.usableSpace.usable.width} by ${result.usableSpace.usable.depth} by ${result.usableSpace.usable.height} inches.`);
    });

    $$('[data-habitat-back]').forEach((button) => button.addEventListener('click', () => {
      state.step = Math.max(1, state.step - 1);
      persist();
      renderStep(true);
    }));
    $('[data-habitat-generate]').addEventListener('click', generate);
    $$('[data-habitat-go]').forEach((button) => button.addEventListener('click', () => {
      if (button.disabled) return;
      state.step = Number(button.dataset.habitatGo);
      renderStep(true);
    }));
    $$('[data-habitat-view]').forEach((button) => button.addEventListener('click', () => {
      state.view = button.dataset.habitatView;
      persist();
      renderStage();
      announce(`${engine.humanize(state.view)} view.`);
    }));
    $$('[data-habitat-zoom]').forEach((button) => button.addEventListener('click', () => {
      const levels = [0.75, 1, 1.25, 1.5];
      const index = levels.indexOf(state.zoom);
      state.zoom = levels[clamp(index + Number(button.dataset.habitatZoom), 0, levels.length - 1)];
      persist();
      renderStage();
    }));
    $$('[data-habitat-pan]').forEach((button) => button.addEventListener('click', () => {
      const wrap = $('.habitat-studio__stage-wrap');
      const distance = 96;
      const direction = button.dataset.habitatPan;
      wrap?.scrollBy?.({
        left: direction === 'left' ? -distance : direction === 'right' ? distance : 0,
        top: direction === 'up' ? -distance : direction === 'down' ? distance : 0,
        behavior: root.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
      announce(`Canvas panned ${direction}.`);
    }));
    $$('[data-habitat-orbit]').forEach((button) => button.addEventListener('click', () => {
      state.view = 'perspective';
      state.orbit = clamp(state.orbit + Number(button.dataset.habitatOrbit) * 8, -45, 45);
      persist();
      renderStage();
      announce(`Perspective orbit ${Math.abs(state.orbit)} degrees ${state.orbit < 0 ? 'left' : 'right'}.`);
    }));
    $('[data-habitat-reset]').addEventListener('click', () => {
      if (!state.originalLayout) return;
      pushHistory();
      state.layout = clone(state.originalLayout);
      state.selectedId = state.layout.placements[0]?.id || null;
      commitPlacementChange('Layout reset to the agent’s original measured arrangement.');
    });
    $('[data-habitat-undo]').addEventListener('click', () => {
      if (!history.length) return;
      future.push(clone(state.layout.placements));
      state.layout.placements = history.pop();
      commitPlacementChange('Last layout change undone.');
    });
    $('[data-habitat-redo]').addEventListener('click', () => {
      if (!future.length) return;
      history.push(clone(state.layout.placements));
      state.layout.placements = future.pop();
      commitPlacementChange('Layout change restored.');
    });

    $('[data-habitat-pieces]').addEventListener('click', (event) => {
      const piece = event.target.closest('[data-habitat-piece]');
      if (!piece) return;
      state.selectedId = piece.dataset.habitatPiece;
      persist();
      renderWorkspace();
    });
    $('[data-habitat-piece-list]').addEventListener('click', (event) => {
      const piece = event.target.closest('[data-habitat-list-piece]');
      if (!piece) return;
      state.selectedId = piece.dataset.habitatListPiece;
      renderWorkspace();
      $('[data-habitat-stage]').focus();
    });
    $('[data-habitat-inspector]').addEventListener('click', (event) => {
      const selected = selectedPlacement();
      if (!selected) return;
      if (event.target.closest('[data-habitat-rotate]')) {
        if (selected.transform.locked) return;
        pushHistory();
        selected.transform.rotation.y = (selected.transform.rotation.y + 90) % 360;
        commitPlacementChange(`${selected.title} rotated. ${fitLabel(selected.fit?.status)}.`);
      }
      if (event.target.closest('[data-habitat-lock]')) {
        pushHistory();
        selected.transform.locked = !selected.transform.locked;
        commitPlacementChange(`${selected.title} ${selected.transform.locked ? 'locked' : 'unlocked'}.`);
      }
      if (event.target.closest('[data-habitat-remove]')) {
        pushHistory();
        state.layout.placements = state.layout.placements.filter((placement) => placement.id !== selected.id);
        state.selectedId = state.layout.placements[0]?.id || null;
        commitPlacementChange(`${selected.title} removed from the local layout. Shopify cart unchanged.`);
      }
      if (event.target.closest('[data-habitat-replace]')) replaceSelected();
    });

    $('[data-habitat-stage]').addEventListener('keydown', (event) => {
      const selected = selectedPlacement();
      if (!selected) return;
      const axes = viewAxes();
      const increment = event.shiftKey ? 1 : 0.25;
      if (event.key === 'ArrowLeft') moveSelected(axes.x, -increment);
      else if (event.key === 'ArrowRight') moveSelected(axes.x, increment);
      else if (event.key === 'ArrowDown') moveSelected(axes.y, -increment);
      else if (event.key === 'ArrowUp') moveSelected(axes.y, increment);
      else if (event.key.toLowerCase() === 'r' && !selected.transform.locked) {
        pushHistory();
        selected.transform.rotation.y = (selected.transform.rotation.y + 90) % 360;
        commitPlacementChange(`${selected.title} rotated.`);
      } else if (event.key.toLowerCase() === 'l') {
        pushHistory();
        selected.transform.locked = !selected.transform.locked;
        commitPlacementChange(`${selected.title} ${selected.transform.locked ? 'locked' : 'unlocked'}.`);
      } else return;
      event.preventDefault();
    });

    $('[data-habitat-stage]').addEventListener('pointerdown', (event) => {
      const pieceButton = event.target.closest('[data-habitat-piece]');
      if (!pieceButton) return;
      state.selectedId = pieceButton.dataset.habitatPiece;
      const selected = selectedPlacement();
      if (!selected || selected.transform.locked) return;
      pushHistory();
      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        position: clone(selected.transform.position)
      };
      pieceButton.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    });
    $('[data-habitat-stage]').addEventListener('pointermove', (event) => {
      if (!drag || drag.pointerId !== event.pointerId) return;
      const selected = selectedPlacement();
      const rect = $('[data-habitat-stage]').getBoundingClientRect();
      const axes = viewAxes();
      const limits = limitsForView();
      selected.transform.position[axes.x] = Math.max(0, drag.position[axes.x] + ((event.clientX - drag.startX) / rect.width) * limits.width);
      selected.transform.position[axes.y] = Math.max(0, drag.position[axes.y] - ((event.clientY - drag.startY) / rect.height) * limits.height);
      revalidate();
      renderStage();
      renderInspector();
    });
    const finishDrag = (event) => {
      if (!drag || drag.pointerId !== event.pointerId) return;
      drag = null;
      commitPlacementChange('Piece moved and fit rechecked.');
    };
    $('[data-habitat-stage]').addEventListener('pointerup', finishDrag);
    $('[data-habitat-stage]').addEventListener('pointercancel', finishDrag);

    $('[data-habitat-photo]').addEventListener('change', (event) => {
      const file = event.target.files?.[0];
      const consent = form.elements.habitat_photo_consent.checked;
      if (!file) return;
      if (!consent) {
        event.target.value = '';
        showError(1, 'Confirm local-session image use before previewing the photo.');
        return;
      }
      if (photoUrl) URL.revokeObjectURL(photoUrl);
      photoUrl = URL.createObjectURL(file);
      const metadata = engine.createVisionInput(file, true);
      const preview = $('[data-habitat-photo-preview]');
      preview.hidden = false;
      preview.querySelector('img').src = photoUrl;
      preview.querySelector('dl').innerHTML = `
        <div><dt>File</dt><dd>${escapeHtml(metadata.name)}</dd></div>
        <div><dt>Storage</dt><dd>Local preview only</dd></div>
        <div><dt>Scale</dt><dd>Unknown without calibration</dd></div>
      `;
      showError(1);
      announce('Reference photo previewed locally. It was not uploaded.');
    });
    $('[data-habitat-photo-delete]').addEventListener('click', () => {
      if (photoUrl) URL.revokeObjectURL(photoUrl);
      photoUrl = null;
      form.elements.habitat_photo.value = '';
      form.elements.habitat_photo_consent.checked = false;
      const preview = $('[data-habitat-photo-preview]');
      preview.hidden = true;
      preview.querySelector('img').removeAttribute('src');
      announce('Local reference photo deleted.');
    });

    $('[data-habitat-concept-request]').addEventListener('click', () => {
      const current = state.concept?.state || 'NOT_REQUESTED';
      if (current === 'NOT_REQUESTED' || current === 'FAILED' || current === 'EXPIRED' || current === 'COMPLETE') {
        state.concept = { ...engine.createConcept(state.layout, 'READY'), id: `concept-${state.layout.layoutId}` };
        persist();
        renderConcept();
        announce('Concept placeholder is ready. It will not be used as proof of fit.');
        return;
      }
      if (current === 'READY') {
        state.concept.state = engine.conceptTransition('READY', 'generate');
        renderConcept();
        root.setTimeout(() => {
          state.concept.state = engine.conceptTransition('GENERATING', 'complete');
          persist();
          renderConcept();
          announce(engine.DISCLAIMER);
        }, root.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 10 : 420);
      }
    });
    $('[data-habitat-notify]').addEventListener('click', () => {
      state.notifyIntent = true;
      persist();
      announce('Notify-me intent saved on this device. No notification service is connected.');
    });
    $('[data-habitat-accept]').addEventListener('click', acceptBuild);

    root.document.addEventListener('corkopia:products-ready', () => {
      if (state.step === 3 && state.layout) renderWorkspace();
    });
    root.addEventListener?.('beforeunload', () => {
      if (photoUrl) URL.revokeObjectURL(photoUrl);
    });

    restoreForm();
    const catalog = form.elements.habitat_catalog.value;
    $('[data-enclosure-model]').hidden = catalog === 'custom';
    renderStep();
    root.document.dispatchEvent(new CustomEvent('corkopia:habitat-studio-ready', {
      detail: {
        version: engine.VERSION,
        fitAuthority: 'deterministic_fit_engine',
        provider: 'none',
        productCount: catalogProducts(studio).length,
        deployed: false
      }
    }));
  };

  return Object.freeze({
    STORAGE_KEY,
    HANDOFF_KEY,
    createState,
    sanitizeState,
    readState,
    saveState,
    validateEnclosure,
    prepare
  });
});
